import { Button, Card, Pagination, Tag } from "@arco-design/web-react";
import { Ban, CheckCircle2, Eye, Pencil, Plus } from "lucide-react";

import { supplierBasicPageSizeOptions, type SupplierKindMeta } from "../config";
import type { SupplierItem, SupplierKind } from "../types";
import "./SupplyBasicPage.css";

type SupplyBasicResultTableProps = {
  kind: SupplierKind;
  meta: SupplierKindMeta;
  items: SupplierItem[];
  loading: boolean;
  page: number;
  pageSize: number;
  total: number;
  canManage: boolean;
  onCreate: () => void;
  onEdit: (item: SupplierItem) => void;
  onViewDetails: (item: SupplierItem) => void;
  onDisable: (item: SupplierItem) => void;
  onEnable: (item: SupplierItem) => void;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
};

/** 供应商基础设置结果表。 */
export function SupplyBasicResultTable(props: SupplyBasicResultTableProps) {
  const {
    kind,
    meta,
    items,
    loading,
    page,
    pageSize,
    total,
    canManage,
    onCreate,
    onEdit,
    onViewDetails,
    onDisable,
    onEnable,
    onPageChange,
    onPageSizeChange,
  } = props;
  const columnCount =
    6 +
    (kind === "guides" ||
      kind === "buses" ||
      kind === "hotels" ||
      kind === "grounds" ||
      kind === "restaurants"
      ? 1
      : 0) +
    (meta.showParentGround ? 1 : 0);

  return (
    <Card className="supply-basic-card" bodyStyle={{ padding: 0 }}>
      <div className="supply-basic-results">
        <div className="supply-basic-results-header">
          <h2>{meta.title}供应商</h2>
          {canManage ? (
            <Button icon={<Plus size={16} />} onClick={onCreate}>
              {meta.createLabel}
            </Button>
          ) : null}
        </div>
        <div className="supply-basic-table-scroll">
          <table className="supply-basic-table">
            <thead>
              <tr>
                <th>名称</th>
                <th>编号</th>
                <th>省市</th>
                {kind === "guides" ? <th>导游证号</th> : null}
                {kind === "buses" ? <th>规模</th> : null}
                {kind === "hotels" ? <th>档次</th> : null}
                {kind === "grounds" ? <th>规模</th> : null}
                {meta.showParentGround ? <th>所属地接</th> : null}
                <th>状态</th>
                <th>更新时间</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={columnCount} className="supply-basic-empty">
                    正在加载{meta.title}...
                  </td>
                </tr>
              ) : null}
              {!loading && items.length === 0 ? (
                <tr>
                  <td colSpan={columnCount} className="supply-basic-empty">
                    暂无{meta.title}数据
                  </td>
                </tr>
              ) : null}
              {!loading
                ? items.map((item) => (
                    <tr key={item.supplierId}>
                      <td>{item.supplierName}</td>
                      <td>{item.supplierCode}</td>
                      <td>
                        {[item.province, item.city].filter(Boolean).join(" / ") || "-"}
                      </td>
                      {kind === "guides" ? (
                        <td>{item.guide?.guideCredentialNo || "-"}</td>
                      ) : null}
                      {kind === "buses" ? <td>{item.bus?.scaleText || "-"}</td> : null}
                      {kind === "hotels" ? (
                        <td>{item.hotel?.housLevelName || "-"}</td>
                      ) : null}
                      {kind === "grounds" ? (
                        <td>{item.ground?.scaleText || "-"}</td>
                      ) : null}
                      {meta.showParentGround ? (
                        <td>{item.parentSupplierName || "-"}</td>
                      ) : null}
                      <td>
                        <Tag color={item.isDisabled ? "orangered" : "green"}>
                          {item.isDisabledLabel}
                        </Tag>
                      </td>
                      <td>{item.updatedAt}</td>
                      <td>
                        <div className="supply-basic-row-actions">
                          <Button
                            type="text"
                            shape="circle"
                            aria-label={`查看详情 ${item.supplierName}`}
                            title="查看详情"
                            icon={<Eye size={16} />}
                            onClick={() => onViewDetails(item)}
                          />
                          {canManage ? (
                            <>
                          <Button
                            type="text"
                            shape="circle"
                            aria-label={`编辑 ${item.supplierName}`}
                            title="编辑"
                            icon={<Pencil size={16} />}
                            onClick={() => onEdit(item)}
                          />
                          {item.isDisabled ? (
                            <Button
                              type="text"
                              shape="circle"
                              aria-label={`启用 ${item.supplierName}`}
                              title="启用"
                              icon={<CheckCircle2 size={16} />}
                              onClick={() => onEnable(item)}
                            />
                          ) : (
                            <Button
                              type="text"
                              shape="circle"
                              aria-label={`停用 ${item.supplierName}`}
                              title="停用"
                              icon={<Ban size={16} />}
                              onClick={() => onDisable(item)}
                            />
                          )}
                            </>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  ))
                : null}
            </tbody>
          </table>
        </div>
        <div className="supply-basic-pagination">
          <Pagination
            total={total}
            current={page}
            pageSize={pageSize}
            sizeOptions={supplierBasicPageSizeOptions}
            showTotal
            showJumper
            sizeCanChange
            onChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
          />
        </div>
      </div>
    </Card>
  );
}
